"use client";

import { useState, FormEvent } from "react";
import { PencilIcon } from "@heroicons/react/24/outline";
import { Session } from "next-auth";
import { UserProfile } from "@/utils/types";
import { updateProfileService } from "@/services/profile.service.ts";
import { convertToBase64 } from "@/utils/files.ts";

const MAX_FILE_SIZE = 300 * 1024; // 300KB
const ALLOWED_FILE_TYPES = ["image/jpg", "image/jpeg", "image/png", "image/gif", "image/webp"];

// --- Type guards for provider profiles ---

export interface GoogleProfile {
  picture?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  sub?: string;
}

export interface DiscordProfile {
  avatar?: string | null;
  username?: string;
}

export const hasProp = (obj: unknown, prop: string): boolean =>
  !!obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, prop);

export function isGoogleProfile(p: unknown): p is GoogleProfile {
  return hasProp(p, "picture") || hasProp(p, "given_name") || hasProp(p, "name");
}

export function isDiscordProfile(p: unknown): p is DiscordProfile {
  return hasProp(p, "avatar") || hasProp(p, "username");
}

// --- Avatar helper ---

function resolveProviderAvatar(profileData: UserProfile | null): string | null {
  if (!profileData?.linkedAccounts) return null;
  for (const acct of profileData.linkedAccounts) {
    const profile = acct.providerProfile;
    if (acct.provider === "google" && isGoogleProfile(profile) && profile.picture) {
      return profile.picture;
    }
    if (acct.provider === "discord" && isDiscordProfile(profile) && profile.avatar) {
      const id = acct.providerId as string;
      if (profile.avatar && id) {
        return `https://cdn.discordapp.com/avatars/${id}/${profile.avatar}.png`;
      }
    }
  }
  return null;
}

function getDiceBearUrl(seed: string, size = 96): string {
  return `https://api.dicebear.com/9.x/rings/svg?seed=${encodeURIComponent(seed)}&size=${String(size)}&backgroundColor=0a0a0a`;
}

function ProfileAvatar({ profileData, session }: { profileData: UserProfile | null; session: Session | null }) {
  const providerAvatar = resolveProviderAvatar(profileData);
  const diceBearSeed = session?.user.id || session?.user.email || "anon";

  const src = profileData?.avatar
    ? profileData.avatar
    : providerAvatar
      ? providerAvatar
      : getDiceBearUrl(diceBearSeed);

  return (
    <div
      style={{
        width: "52px",
        height: "52px",
        borderRadius: "50%",
        background: "#181818",
        border: "1px solid #222",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt="User avatar"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    </div>
  );
}

// --- Props ---

interface PersonalInfoSectionProps {
  profileData: UserProfile | null;
  session: Session | null;
  onProfileUpdated: () => void;
  onError: (message: string) => void;
}

export function PersonalInfoSection({
  profileData,
  session,
  onProfileUpdated,
  onError,
}: PersonalInfoSectionProps) {
  const [isEditing, setIsEditing] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const imageFile = formData.get("avatar");

    if (imageFile instanceof File && imageFile.size > 0) {
      if (!ALLOWED_FILE_TYPES.includes(imageFile.type)) {
        onError("Please upload a valid image file (JPG, JPEG, PNG, GIF, or WebP)");
        return;
      }
      if (imageFile.size > MAX_FILE_SIZE) {
        onError("Image size must be less than 300KB");
        return;
      }
      const base64Image = await convertToBase64(imageFile);
      formData.delete("avatar");
      formData.append("avatar", base64Image);
    } else {
      formData.delete("avatar");
    }

    try {
      const jsonData = Object.fromEntries(formData.entries());
      await updateProfileService(jsonData);
      onProfileUpdated();
      setIsEditing(false);
    } catch (err) {
      console.error("[PersonalInfoSection] Error updating profile:", err);
    }
  }

  const displayName =
    profileData?.name ??
    profileData?.emails?.[0]?.email ??
    session?.user.email ??
    "Anon";

  const subText = profileData?.phoneNumber || profileData?.email || "";

  return (
    <div
      style={{
        background: "#0e0e0e",
        border: "1px solid #1c1c1c",
        borderRadius: "14px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 20px",
          borderBottom: "1px solid #1c1c1c",
        }}
      >
        <div>
          <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", color: "#c8c3ba", margin: 0 }}>
            PERSONAL INFO
          </p>
          <p style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.02em", marginTop: "2px" }}>
            Your profile and display name
          </p>
        </div>
        <button
          onClick={() => { setIsEditing((prev) => !prev); }}
          style={{
            fontSize: "10px",
            fontWeight: 500,
            letterSpacing: "0.08em",
            color: "#888",
            background: "#141414",
            border: "1px solid #222",
            borderRadius: "7px",
            padding: "6px 14px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            transition: "border-color 0.15s, color 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#272727";
            e.currentTarget.style.color = "#d4cfc6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#222";
            e.currentTarget.style.color = "#888";
          }}
        >
          <PencilIcon style={{ width: "10px", height: "10px" }} />
          {isEditing ? "CANCEL" : "EDIT"}
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: "18px 20px" }}>
        {isEditing ? (
          <form onSubmit={(e) => { void handleSubmit(e); }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
              <ProfileAvatar profileData={profileData} session={session} />
              <div>
                <p style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.06em", marginBottom: "6px" }}>
                  PROFILE PICTURE
                </p>
                <input
                  id="avatar"
                  name="avatar"
                  type="file"
                  accept="image/*"
                  aria-label="Profile picture"
                  style={{
                    fontSize: "11px",
                    color: "#888",
                    background: "#0a0a0a",
                    border: "1px solid #1c1c1c",
                    borderRadius: "8px",
                    padding: "7px 10px",
                    cursor: "pointer",
                    outline: "none",
                    width: "100%",
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
              <div>
                <label
                  htmlFor="name"
                  style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.06em", display: "block", marginBottom: "6px" }}
                >
                  NAME
                </label>
                <input
                  id="name"
                  name="name"
                  defaultValue={profileData?.name ?? "Anon"}
                  aria-label="Full name"
                  required
                  style={{
                    width: "100%",
                    fontFamily: "monospace",
                    fontSize: "12px",
                    color: "#e8e3da",
                    background: "#0a0a0a",
                    border: "1px solid #1c1c1c",
                    borderRadius: "8px",
                    padding: "9px 14px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="phoneNumber"
                  style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.06em", display: "block", marginBottom: "6px" }}
                >
                  PHONE
                </label>
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  defaultValue={profileData?.phoneNumber ?? ""}
                  type="tel"
                  aria-label="Phone number"
                  style={{
                    width: "100%",
                    fontFamily: "monospace",
                    fontSize: "12px",
                    color: "#e8e3da",
                    background: "#0a0a0a",
                    border: "1px solid #1c1c1c",
                    borderRadius: "8px",
                    padding: "9px 14px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.06em",
                color: "#051a0e",
                background: "#3ecf7e",
                border: "none",
                borderRadius: "8px",
                padding: "9px 18px",
                cursor: "pointer",
              }}
            >
              Save changes
            </button>
          </form>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <ProfileAvatar profileData={profileData} session={session} />
            <div>
              <p style={{ fontSize: "15px", fontWeight: 500, color: "#e8e3da", margin: 0 }}>
                {displayName}
              </p>
              {subText && (
                <p style={{ fontSize: "11px", color: "#4a4a4a", marginTop: "3px" }}>
                  {subText}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
